import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const HeaderSlotContext = createContext(null);

/**
 * Layout's fixed header hands its portal slots down as nodes, not ids.
 *
 * A `getElementById` at effect time cannot be trusted here: the phone shell
 * unmounts the app header outright, and on the way back to desktop the header
 * and the page component that fills it re-render in separate commits, so the
 * page's effect can run while the slot does not exist yet — and never runs
 * again. Passing the node through React turns its arrival into an ordinary
 * update, whichever commit it lands in.
 */
// eslint-disable-next-line react-refresh/only-export-components -- context file: Provider, hook and context are colocated by convention
export const useHeaderSlotRegistry = () => {
  const [slots, setSlots] = useState({});
  const slotRefs = useRef({});

  // One stable callback per id, so a re-render does not detach and reattach
  // the ref (which would churn state on every commit).
  const slotRef = useCallback((id) => {
    slotRefs.current[id] ??= (node) => {
      setSlots((previous) => (
        previous[id] === (node ?? null) ? previous : { ...previous, [id]: node ?? null }
      ));
    };
    return slotRefs.current[id];
  }, []);

  return useMemo(() => ({ slots, slotRef }), [slots, slotRef]);
};

export const HeaderSlotProvider = ({ registry, children }) => (
  <HeaderSlotContext.Provider value={registry}>{children}</HeaderSlotContext.Provider>
);

/** The header slot node for `id`, or null while the header does not render one. */
// eslint-disable-next-line react-refresh/only-export-components -- context file: Provider, hook and context are colocated by convention
export const useHeaderPortalSlot = (id) => useContext(HeaderSlotContext)?.slots[id] ?? null;
